import { showModalForm, ModalForm } from '@components/modal-form';
import { FormGroup } from '@components/async-form';
import { ThemeFont, ElementType, PageTheme, PreviewStyle } from './types';
import {
  FONT_SIZE_MIN,
  FONT_SIZE_MAX,
  LINE_HEIGHT_MIN,
  LINE_HEIGHT_MAX,
  LINE_HEIGHT_STEP,
  MARGIN_BOTTOM_MIN,
  MARGIN_BOTTOM_MAX,
  MARGIN_BOTTOM_STEP,
} from './constants';
import { getFontFamily } from './utils';

export type TypographyState = {
  size: number;
  leading: number;
  marginBottom: number;
};

type TypographyControlsProps = {
  selectedElementType: ElementType;
  state: TypographyState;
  setState: (updates: Partial<TypographyState>) => void;
};

export function TypographyControls({
  selectedElementType,
  state,
  setState,
}: TypographyControlsProps) {
  return (
    <div class="p-6 bg-white border rounded-lg">
      <h4 class="font-medium text-gray-900 mb-4">
        {selectedElementType === 'content' ? 'Body Text' : selectedElementType.toUpperCase()}{' '}
        Settings
      </h4>

      <div class="flex flex-col gap-6">
        {/* Font Size */}
        <div class="flex flex-col gap-2">
          <label class="block text-sm font-medium text-gray-700">Font Size</label>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={FONT_SIZE_MIN}
              max={FONT_SIZE_MAX}
              step="1"
              value={state.size}
              onInput={(e) => setState({ size: Number((e.target as HTMLInputElement).value) })}
              class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span class="text-sm text-gray-500 min-w-[3rem] font-mono">{state.size}px</span>
          </div>
        </div>

        {/* Line Height */}
        <div class="flex flex-col gap-2">
          <label class="block text-sm font-medium text-gray-700">Line Height</label>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={LINE_HEIGHT_MIN}
              max={LINE_HEIGHT_MAX}
              step={LINE_HEIGHT_STEP}
              value={state.leading}
              onInput={(e) => setState({ leading: Number((e.target as HTMLInputElement).value) })}
              class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span class="text-sm text-gray-500 min-w-[3rem] font-mono">
              {state.leading.toFixed(1)}
            </span>
          </div>
        </div>

        {/* Margin Bottom */}
        <div class="flex flex-col gap-2">
          <label class="block text-sm font-medium text-gray-700">Bottom Spacing</label>
          <div class="flex items-center gap-3">
            <input
              type="range"
              min={MARGIN_BOTTOM_MIN}
              max={MARGIN_BOTTOM_MAX}
              step={MARGIN_BOTTOM_STEP}
              value={state.marginBottom}
              onInput={(e) =>
                setState({ marginBottom: Number((e.target as HTMLInputElement).value) })
              }
              class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span class="text-sm text-gray-500 min-w-[3rem] font-mono">{state.marginBottom}px</span>
          </div>
        </div>
      </div>
    </div>
  );
}

type FontSelectorProps = {
  allFonts: ThemeFont[];
  loading: boolean;
  selectedElementType: ElementType;
  previewStyles: Partial<Record<ElementType, PreviewStyle>>; // Temporary preview changes before saving
  localTheme: PageTheme; // Current saved theme configuration
  onFontSelect: (font: ThemeFont) => void;
  onAddCustomFont: () => void;
};

function scrollIntoView(el: HTMLElement | null) {
  if (!el || el.dataset.scrolled) {
    return;
  }

  el.dataset.scrolled = 'y';
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export function FontSelector({
  allFonts,
  loading,
  selectedElementType,
  previewStyles,
  localTheme,
  onFontSelect,
  onAddCustomFont,
}: FontSelectorProps) {
  // Get the currently selected font for this element
  // Priority: preview styles (temporary changes) > applied theme fonts > default font
  const previewFontId = previewStyles[selectedElementType]?.fontId;
  const appliedFontId = localTheme.appliedFonts?.[selectedElementType]?.fontId;
  const currentFontId = previewFontId || appliedFontId || 'inter';

  return (
    <div class="h-full flex flex-col">
      <div class="p-6 border-b">
        <div class="flex items-center justify-between">
          <h4 class="font-medium text-gray-900">Font Family</h4>
          <button class="text-sm text-blue-600 hover:text-blue-700" onClick={onAddCustomFont}>
            + Add Custom Font
          </button>
        </div>
      </div>

      <div class="p-6 flex-1 overflow-y-auto">
        {loading ? (
          <div class="flex flex-col gap-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} class="h-16 bg-gray-100 animate-pulse rounded-md" />
            ))}
          </div>
        ) : (
          <div
            class="flex flex-col gap-3"
            key={`${allFonts.length}-${currentFontId}-${selectedElementType}`}
          >
            {allFonts.map((font) => {
              const isSelected = currentFontId === font.id;

              return (
                <button
                  key={font.id}
                  ref={(el) => {
                    if (isSelected) {
                      scrollIntoView(el);
                    }
                  }}
                  class={`w-full p-4 rounded-lg border text-left transition-all ${
                    isSelected
                      ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => onFontSelect(font)}
                >
                  <div class="text-sm font-medium text-gray-700 mb-2">{font.name}</div>
                  <div class="text-xl text-gray-900" style={{ fontFamily: getFontFamily(font) }}>
                    The quick brown fox jumps
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

type AddCustomFontOpts = {
  allFonts: ThemeFont[];
  setAllFonts: (updater: (fonts: ThemeFont[]) => ThemeFont[]) => void;
  setLocalTheme: (updater: (theme: PageTheme) => PageTheme) => void;
  selectedElementType: ElementType;
  setPreviewStyles: (styles: any) => void;
};

export async function handleAddCustomFont(opts: AddCustomFontOpts) {
  const { allFonts, setAllFonts, setLocalTheme, selectedElementType, setPreviewStyles } = opts;
  const result = await showModalForm<{ name: string; url: string }>(({ resolve }) => (
    <ModalForm
      title="Add Custom Font"
      confirmButtonText="Add Font"
      onSubmit={async (data) => {
        resolve(data);
      }}
    >
      <FormGroup prop="name">
        <label class="block text-sm font-medium text-gray-700 mb-2">Font Name *</label>
        <input
          name="name"
          type="text"
          placeholder="e.g. Roboto, Open Sans"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p class="text-xs text-gray-500 mt-1">The CSS font-family name (case-sensitive)</p>
      </FormGroup>

      <FormGroup prop="url">
        <label class="block text-sm font-medium text-gray-700 mb-2">Font URL *</label>
        <input
          name="url"
          type="url"
          placeholder="https://fonts.googleapis.com/css2?family=..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p class="text-xs text-gray-500 mt-1">Google Fonts URL or other CSS font import URL</p>
      </FormGroup>
    </ModalForm>
  ));

  if (!result) {
    return;
  }

  const { name, url } = result;

  // Validate inputs
  const trimmedName = name.trim();
  const trimmedUrl = url.trim();

  if (!trimmedName || !trimmedUrl) {
    alert('Please fill in both font name and URL.');
    return;
  }

  // Basic URL validation
  if (
    !trimmedUrl.startsWith('http://') &&
    !trimmedUrl.startsWith('https://') &&
    !trimmedUrl.startsWith('/')
  ) {
    alert('Please enter a valid URL (starting with http://, https://, or / for relative paths).');
    return;
  }

  // Check for duplicate font names
  if (allFonts.some((f) => f.name.toLowerCase() === trimmedName.toLowerCase())) {
    alert(`A font named "${trimmedName}" already exists. Please choose a different name.`);
    return;
  }

  // Create a new custom font entry
  const customFont: ThemeFont = {
    id: crypto.randomUUID(),
    url: trimmedUrl,
    name: trimmedName,
  };

  // Prepend to all fonts list (custom fonts appear at top)
  setAllFonts((currentFonts) => [customFont, ...currentFonts]);

  // Add to theme fonts
  setLocalTheme((theme) => ({
    ...theme,
    fonts: [...(theme.fonts || []), customFont],
  }));

  // Auto-select and apply the new font to the currently selected element
  setPreviewStyles((s: any) => ({
    ...s,
    [selectedElementType]: { ...s[selectedElementType], fontId: customFont.id },
  }));
}
